// src/encounter/lib/l360-history-review.ts
// L360 I2: the T02 "History & reconciliation" template, read as one state per section for the
// `history.review_submitted` event. Pure: no network, no React. The sender is
// services/use-l360-history-review-event.ts.
//
// T02's shape: a root group coded longevity360-template T02, a display guide, then pairs of a value item
// `<root>-<id>` and a companion choice `<root>-<id>-state` (unknown | asked-declined | not-applicable). A section
// is every item whose linkId + "-state" also exists, so the 13 ids are read from the template, never listed here.
import type { FhirQuestionnaire, FhirQuestionnaireItem } from '@/types/template-studio-api';
import type { QRAnswerWire, QRItemWire, QuestionnaireResponseResource } from '@/types/questionnaire-response-save-api';

export const L360_TEMPLATE_SYSTEM = 'https://fhir.scrips.com/CodeSystem/longevity360-template';
export const L360_HISTORY_TEMPLATE_CODE = 'T02';
const FIELD_SYSTEM = 'https://fhir.scrips.com/CodeSystem/longevity360-field';
const STATE_SUFFIX = '-state';

export type HistorySectionState = 'known' | 'unknown' | 'declined' | 'not_applicable' | 'missing';
export interface HistorySection {
  id: string;
  state: HistorySectionState;
}

export const CHIP_STATE: Record<string, HistorySectionState> = {
  unknown: 'unknown',
  'asked-declined': 'declined',
  'not-applicable': 'not_applicable',
};

function findRoot(items: readonly FhirQuestionnaireItem[] | null | undefined): FhirQuestionnaireItem | null {
  for (const it of items ?? []) {
    if (it.code?.some((c) => c.system === L360_TEMPLATE_SYSTEM && c.code === L360_HISTORY_TEMPLATE_CODE)) return it;
    const inner = findRoot(it.item);
    if (inner) return inner;
  }
  return null;
}

/** True when the template has a group coded longevity360-template T02. Matches the code, never a GUID. */
export function isL360HistoryTemplate(questionnaire: FhirQuestionnaire | null | undefined): boolean {
  return findRoot(questionnaire?.item) !== null;
}

export function collectItems(items: readonly FhirQuestionnaireItem[] | null | undefined, out: FhirQuestionnaireItem[]): FhirQuestionnaireItem[] {
  for (const it of items ?? []) {
    out.push(it);
    collectItems(it.item, out);
  }
  return out;
}

export function indexResponse(items: readonly QRItemWire[] | undefined, out: Map<string, QRAnswerWire[]>): Map<string, QRAnswerWire[]> {
  for (const it of items ?? []) {
    if (it.answer?.length) out.set(it.linkId, [...(out.get(it.linkId) ?? []), ...it.answer]);
    indexResponse(it.item, out);
  }
  return out;
}

export const hasValue = (a: QRAnswerWire) =>
  (typeof a.valueString === 'string' && a.valueString.trim() !== '') ||
  typeof a.valueBoolean === 'boolean' ||
  typeof a.valueInteger === 'number' ||
  typeof a.valueDecimal === 'number' ||
  (typeof a.valueDateTime === 'string' && a.valueDateTime !== '') ||
  !!a.valueCoding?.code;

/**
 * One `{ id, state }` per section, in template order. `known` wins when a value is present (a whitespace-only string is
 * no value). Otherwise the companion chip decides: unknown, declined, not_applicable. Neither: `missing`.
 */
export function summarizeHistorySections(
  questionnaire: FhirQuestionnaire | null | undefined,
  storedQr: QuestionnaireResponseResource | null | undefined,
): HistorySection[] {
  const root = findRoot(questionnaire?.item);
  if (!root) return [];
  const items = collectItems(root.item, []);
  const linkIds = new Set(items.map((i) => i.linkId));
  const answers = indexResponse(storedQr?.item, new Map());
  const sections: HistorySection[] = [];
  for (const item of items) {
    const stateId = item.linkId + STATE_SUFFIX;
    if (item.linkId.endsWith(STATE_SUFFIX) || !linkIds.has(stateId)) continue;
    // The id is the field code `T02.<id>` when the item has one, else the linkId after the root's prefix.
    const field = item.code?.find((c) => c.system === FIELD_SYSTEM)?.code;
    const prefix = `${L360_HISTORY_TEMPLATE_CODE}.`;
    const id = field?.startsWith(prefix) ? field.slice(prefix.length) : item.linkId.slice(root.linkId.length + 1);
    let state: HistorySectionState = 'missing';
    if ((answers.get(item.linkId) ?? []).some(hasValue)) state = 'known';
    else {
      const chip = (answers.get(stateId) ?? []).map((a) => a.valueCoding?.code ?? '').find((c) => c in CHIP_STATE);
      if (chip) state = CHIP_STATE[chip];
    }
    sections.push({ id, state });
  }
  return sections;
}

/**
 * A short deterministic string of the reviewer and the states (FNV-1a over "reviewer=<id>|id=state|..." in hex).
 * The same reviewer and states give the same revision; another reviewer is another review.
 */
export function historyRevision(sections: readonly HistorySection[], reviewerId?: string): string {
  const text = `reviewer=${reviewerId ?? ''}|` + sections.map((s) => `${s.id}=${s.state}`).join('|');
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

// The review moment is the last required answer. Spec: "Missing required answer: save draft but block review-ready
// event". All 13 T02 sections are required, and a chip (unknown, declined, not applicable) is a real answer. The
// reconciler signature must be a value. The section is found by its id from the summarizer output, not by a hard-coded linkId.
export const HISTORY_RECONCILER_ID = 'reconciler';

/**
 * True only when the sections are not empty, the reconciler is `known`, and no section is `missing`. Spec: "Missing
 * required answer: save draft but block review-ready event". Unknown, declined and not_applicable count as answered.
 * Anything else is a draft: no event. Workflows records I3 from the FIRST post, so an early post would stick the run.
 */
export function isHistoryReviewReady(sections: readonly HistorySection[]): boolean {
  if (sections.length === 0) return false;
  if (sections.find((s) => s.id === HISTORY_RECONCILER_ID)?.state !== 'known') return false;
  return sections.every((s) => s.state !== 'missing');
}
